import React, { useState } from "react";
import {
  QUEST_DASHBOARD_CARDS,
  QUEST_DASHBOARD_FEATURED,
  QUEST_DASHBOARD_PAGE,
  QUEST_DASHBOARD_RIGHT_RAIL,
  QUEST_VISUAL_HERO,
} from "../../data/questDashboardMockData";

function QuestStatGrid({ stats }) {
  if (!stats || !stats.length) return null;
  return (
    <dl className="questCard__stats">
      {stats.map((s) => (
        <div key={s.label} className="questCard__stat">
          <dt className="questCard__stat-label">{s.label}</dt>
          <dd
            className={
              s.accent === "blue"
                ? "questCard__stat-val questCard__stat-val--blue"
                : s.accent === "purple" || s.highlight === "epic"
                  ? "questCard__stat-val questCard__stat-val--purple"
                  : "questCard__stat-val"
            }
          >
            {s.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function FeaturedAiVisual({ image, imagePosition, fallbackClass }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="aiStorytellerCard__visual">
      {!broken && image ? (
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          className="aiStorytellerCard__visual-img"
          style={{ objectPosition: imagePosition || "center" }}
          onError={() => setBroken(true)}
        />
      ) : (
        <div className={`aiStorytellerCard__visual-fallback ${fallbackClass || ""}`} aria-hidden />
      )}
    </div>
  );
}

function QuestCard({ card }) {
  const [broken, setBroken] = useState(false);
  const tags = card.tags || card.chips;

  return (
    <article className="questCard" aria-labelledby={`quest-card-${card.key}`}>
      <div className="questCard__art">
        {!broken && card.image ? (
          <img
            src={card.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="questCard__art-img"
            style={{ objectPosition: card.imagePosition || "center" }}
            onError={() => setBroken(true)}
          />
        ) : (
          <div className={`questCard__art-fallback ${card.fallbackClass || ""}`} aria-hidden />
        )}
        <div className="questCard__art-scrim" aria-hidden />
        <span className="questCard__number" aria-hidden>
          {card.number}
        </span>
      </div>
      <div className="questCard__body">
        <h3 id={`quest-card-${card.key}`} className="questCard__title serif">
          {card.title}
        </h3>
        <p className="questCard__description">{card.description}</p>
        {card.stats ? <QuestStatGrid stats={card.stats} /> : null}
        {tags?.length ? (
          <div className="aiStorytellerCard__chips questCard__tags" role="list">
            {tags.map((t) => (
              <span key={t} className="aiStorytellerCard__chip" role="listitem">
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <button type="button" className="primaryQuestButton questCard__cta">
          {card.cta}
        </button>
      </div>
    </article>
  );
}

export default function QuestDashboardView() {
  const featured = QUEST_DASHBOARD_FEATURED;
  const rail = QUEST_DASHBOARD_RIGHT_RAIL;

  return (
    <main className="qd quests-dash" aria-label="Quests of Treydora">
      <div className="qd-layout">
        <div className="qd-main">
          <section
            className="questsHero"
            style={{ "--quests-hero-image": `url("${QUEST_VISUAL_HERO}")` }}
          >
            <div className="questsHero__overlay" aria-hidden />
            <div className="questsHero__content">
              <h1 className="questsHero__title serif">{QUEST_DASHBOARD_PAGE.pageTitle}</h1>
              <p className="questsHero__intro">{QUEST_DASHBOARD_PAGE.subtitle}</p>

              <div className="aiStorytellerCard">
                <FeaturedAiVisual
                  image={featured.image}
                  imagePosition={featured.imagePosition}
                  fallbackClass={featured.fallbackClass}
                />
                <div className="aiStorytellerCard__copy">
                  <span className="aiStorytellerCard__eyebrow">{featured.eyebrow}</span>
                  <h2 className="aiStorytellerCard__title serif">{featured.title}</h2>
                  <p className="aiStorytellerCard__description">{featured.description}</p>
                  <div className="aiStorytellerCard__chips" role="list">
                    {featured.chips.map((c) => (
                      <span key={c} className="aiStorytellerCard__chip" role="listitem">
                        {c}
                      </span>
                    ))}
                  </div>
                  <div className="aiStorytellerCard__actions">
                    <button type="button" className="primaryQuestButton">
                      {featured.cta}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="questsCardSection" aria-labelledby="quests-card-grid-label">
            <h2 id="quests-card-grid-label" className="sr-only">
              Quest trials
            </h2>
            <div className="questCardGrid">
              {QUEST_DASHBOARD_CARDS.map((card) => (
                <QuestCard key={card.key} card={card} />
              ))}
            </div>
          </section>
        </div>

        <aside className="qd-rail questRail" aria-label="Quest status and activity">
          <section className="questPanel" aria-labelledby="quest-rail-status">
            <h2 id="quest-rail-status" className="questPanel__title serif">
              Quest Status
            </h2>
            <ul className="questPanel__kv">
              <li>
                <span className="questPanel__label">Rank</span>
                <span className="questPanel__val">{rail.questStatus.rank}</span>
              </li>
              <li>
                <span className="questPanel__label">XP</span>
                <span className="questPanel__val">{rail.questStatus.xp}</span>
              </li>
              <li>
                <span className="questPanel__label">Daily Streak</span>
                <span className="questPanel__val">{rail.questStatus.dailyStreak}</span>
              </li>
              <li>
                <span className="questPanel__label">Quest XP</span>
                <span className="questPanel__val">{rail.questStatus.questXp}</span>
              </li>
            </ul>
            <button type="button" className="secondaryQuestButton secondaryQuestButton--wide">
              {rail.questStatus.achievementsCta}
            </button>
          </section>

          <section className="questPanel" aria-labelledby="quest-rail-active">
            <h2 id="quest-rail-active" className="questPanel__title serif">
              Active Quest
            </h2>
            <p className="questPanel__lead">{rail.activeQuest.title}</p>
            <ul className="questPanel__kv">
              <li>
                <span className="questPanel__label">Lore Fragments Unlocked</span>
                <span className="questPanel__val">{rail.activeQuest.loreFragments}</span>
              </li>
              <li>
                <span className="questPanel__label">Faction Status</span>
                <span className="questPanel__val">{rail.activeQuest.factionStatus}</span>
              </li>
            </ul>
          </section>

          <section className="questPanel questPanel--feed" aria-labelledby="quest-rail-activity">
            <h2 id="quest-rail-activity" className="questPanel__title serif">
              Quest Activity
            </h2>
            <ol className="questFeed">
              {rail.activity.map((row) => (
                <li key={`${row.text}-${row.ago}`} className="questFeed__item">
                  <span className="questFeed__dot" aria-hidden />
                  <div className="questFeed__body">
                    <p className="questFeed__text">{row.text}</p>
                    <span className="questFeed__ago">{row.ago}</span>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </main>
  );
}
